(() => {
  if (customElements.get('hmc-vat-declaration')) return;
  const declaration = 'I confirm that the person named above is chronically sick or disabled, the qualifying goods are for their personal or domestic use, and this declaration is accurate. I am completing this for myself or on their behalf.';
  let draft = null;
  let queue = Promise.resolve();
  let checkoutPending = false;

  function attributes(state) {
    const yes = state.choice === 'Yes';
    const confirmed = yes && state.confirmed;
    return {
      'VAT Exempt': state.choice,
      'VAT Exemption Name': yes ? state.name : '',
      'VAT Exemption Reason': yes ? state.reason : '',
      'VAT Exemption Confirmed': confirmed ? 'Yes' : '',
      'VAT Exemption Declaration': confirmed ? declaration : '',
      'VAT Exemption Declared At': confirmed ? new Date().toISOString() : '',
      'VAT Exemption Version': confirmed ? 'HMC-2026-10' : ''
    };
  }

  function persist(state, url) {
    const expected = attributes(state);
    const task = queue.catch(() => {}).then(async () => {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: expected })
      });
      if (!response.ok) throw new Error('Unable to save declaration');
      const cart = await response.json();
      if (!cart.attributes || Object.keys(expected).some(key => (cart.attributes[key] || '') !== expected[key])) {
        throw new Error('Declaration was not confirmed by the server');
      }
      return cart;
    });
    queue = task;
    return task;
  }

  class HmcVatDeclaration extends HTMLElement {
    connectedCallback() {
      this.choiceInputs = this.querySelectorAll('[data-vat-choice]');
      this.nameInput = this.querySelector('[data-vat-name]');
      this.reasonInput = this.querySelector('[data-vat-reason]');
      this.confirmInput = this.querySelector('[data-vat-confirm]');
      this.status = this.querySelector('[data-vat-status]');
      if (draft) {
        this.choiceInputs.forEach(input => { input.checked = input.value === draft.choice; });
        this.nameInput.value = draft.name;
        this.reasonInput.value = draft.reason;
        this.confirmInput.checked = draft.confirmed;
      }
      if (!this.initialised) {
        this.addEventListener('input', () => this.update());
        this.addEventListener('change', () => {
          this.update();
          this.save().catch(() => {
            if (this.isConnected) this.status.textContent = 'We could not save your declaration. Please try checkout again, or call 01482 561964.';
          });
        });
        this.initialised = true;
      }
      this.update();
    }

    state() {
      return {
        choice: this.querySelector('[data-vat-choice]:checked')?.value || '',
        name: this.nameInput.value.trim(),
        reason: this.reasonInput.value.trim(),
        confirmed: this.confirmInput.checked
      };
    }

    message(state = this.state()) {
      if (!state.choice) return 'Please choose whether you are eligible for VAT exemption.';
      if (state.choice !== 'Yes') return 'Please call 01482 561964 so we can help you place your order.';
      if (!state.name) return 'Please enter the full name of the person the goods are for.';
      if (!state.reason) return 'Please describe their qualifying condition.';
      if (!state.confirmed) return 'Please confirm the declaration before proceeding to checkout.';
      return '';
    }

    update() {
      const yes = this.state().choice === 'Yes';
      if (!yes) {
        this.nameInput.value = '';
        this.reasonInput.value = '';
        this.confirmInput.checked = false;
      }
      this.querySelector('[data-vat-details]').hidden = !yes;
      this.querySelector('[data-vat-warning]').hidden = this.state().choice !== 'No';
      [this.nameInput, this.reasonInput, this.confirmInput].forEach(input => {
        input.disabled = !yes;
        input.required = yes;
      });
      draft = this.state();
      const message = this.message(draft);
      document.querySelectorAll('button[name="checkout"][form="cart"]').forEach(button => {
        button.disabled = checkoutPending || Boolean(message);
        button.setAttribute('aria-describedby', 'HmcVatStatus');
      });
      this.status.id = 'HmcVatStatus';
      if (!checkoutPending) this.status.textContent = message || 'Your declaration is complete. You can proceed to checkout.';
    }

    save() {
      return persist(this.state(), this.dataset.updateUrl);
    }

    focusMissing() {
      const state = this.state();
      if (!state.choice || state.choice === 'No') this.choiceInputs[0].focus();
      else if (!state.name) this.nameInput.focus();
      else if (!state.reason) this.reasonInput.focus();
      else this.confirmInput.focus();
    }
  }
  customElements.define('hmc-vat-declaration', HmcVatDeclaration);

  // Capture both button checkout and Enter-key submission before the native form posts.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.id !== 'cart' || (event.submitter && event.submitter.name !== 'checkout')) return;
    const panel = document.querySelector('hmc-vat-declaration');
    if (!panel) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    panel.update();
    const message = panel.message();
    if (message) {
      panel.status.textContent = message;
      panel.focusMissing();
      return;
    }
    const cartBusy = () => Boolean(document.querySelector('#main-cart-items.cart__items--disabled, cart-items.cart__items--disabled'));
    if (cartBusy()) {
      panel.status.textContent = 'Please wait for your basket quantities to finish updating, then try checkout again.';
      return;
    }
    checkoutPending = true;
    panel.update();
    panel.setAttribute('data-saving', '');
    panel.querySelector('fieldset').disabled = true;
    form.inert = true;
    panel.status.textContent = 'Saving your declaration…';
    try {
      await panel.save();
      if (cartBusy()) throw new Error('Basket is still updating');
      panel.querySelector('fieldset').disabled = false;
      form.inert = false;
      let checkout = form.querySelector('input[type="hidden"][name="checkout"]');
      if (!checkout) {
        checkout = document.createElement('input');
        checkout.type = 'hidden';
        checkout.name = 'checkout';
        checkout.value = 'Checkout';
        form.appendChild(checkout);
      }
      HTMLFormElement.prototype.submit.call(form);
    } catch (_) {
      checkoutPending = false;
      form.inert = false;
      panel.querySelector('fieldset').disabled = false;
      panel.removeAttribute('data-saving');
      panel.update();
      panel.status.textContent = 'We could not save your declaration or your basket is still updating. Please try again, or call 01482 561964.';
    }
  }, true);
})();
